import { Link, useSearchParams } from 'react-router-dom';
import SiteHeader from '../../components/feature/SiteHeader';
import SiteFooter from '../../components/feature/SiteFooter';
import ArticleCard from '../../components/feature/ArticleCard';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { getArticleById, getSpecialById, getSpecials } from '../../lib/content';

export default function SpecialPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const specials = getSpecials();
  const idParam = searchParams.get('id');
  const selected = (idParam ? getSpecialById(idParam) : undefined) || specials[0];

  const related = selected.related
    .map((rid) => getSpecialById(rid))
    .filter((s): s is NonNullable<typeof s> => Boolean(s));

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1 w-full">
        <section className="w-full px-4 md:px-8 pt-6 pb-6">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '专题合集' }]} />
            <div className="mt-5">
              <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
                专题合集
              </h1>
              <p className="mt-2 text-sm md:text-base text-foreground-600">
                围绕热点打造的深度策划，集中呈现背景、观点与延伸阅读。
              </p>
            </div>

            {/* 专题切换 */}
            <div className="mt-6 flex flex-wrap gap-2">
              {specials.map((sp) => (
                <button
                  key={sp.id}
                  type="button"
                  onClick={() => setSearchParams({ id: sp.id })}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    selected.id === sp.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600'
                  }`}
                >
                  {sp.title}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* 专题主图导语 */}
        <section className="w-full px-4 md:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="relative rounded-lg overflow-hidden h-56 md:h-72">
              <img
                src={selected.cover}
                alt={selected.title}
                title={`${selected.title} - WWW.17C.COM`}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <span className="inline-block text-xs text-white bg-accent-500 px-2.5 py-1 rounded-md mb-3">
                  {selected.tag}
                </span>
                <h2 className="font-heading font-bold text-2xl md:text-3xl text-white">
                  {selected.title}
                </h2>
                <p className="mt-3 text-sm md:text-base text-white/85 max-w-2xl leading-relaxed">
                  {selected.intro}
                </p>
                <p className="mt-3 text-xs text-white/60">更新于 {selected.updatedAt}</p>
              </div>
            </div>
          </div>
        </section>

        {/* 分组内容 */}
        <section className="w-full px-4 md:px-8 py-10">
          <div className="max-w-6xl mx-auto space-y-12">
            {selected.groups.map((group) => {
              const groupArticles = group.articleIds
                .map((aid) => getArticleById(aid))
                .filter((a): a is NonNullable<typeof a> => Boolean(a));
              return (
                <div key={group.title}>
                  <div className="flex items-center gap-3 mb-5">
                    <span className="w-1 h-6 rounded-full bg-accent-500" />
                    <div>
                      <h3 className="font-heading font-bold text-lg md:text-xl text-foreground-950">
                        {group.title}
                      </h3>
                      <p className="mt-0.5 text-sm text-foreground-500">{group.desc}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {groupArticles.map((article) => (
                      <ArticleCard key={article.id} article={article} variant="grid" />
                    ))}
                  </div>
                </div>
              );
            })}

            {/* 推荐理由 */}
            <div className="rounded-lg bg-primary-50 border border-primary-100 p-6 md:p-8">
              <div className="flex items-start gap-4">
                <span className="shrink-0 w-11 h-11 rounded-md bg-primary-500 text-background-50 flex items-center justify-center text-xl">
                  <i className="ri-lightbulb-line" />
                </span>
                <div>
                  <h3 className="font-heading font-bold text-lg text-primary-900">
                    为什么值得一读
                  </h3>
                  <p className="mt-2 text-sm md:text-base text-foreground-700 leading-relaxed">
                    {selected.reason}
                  </p>
                </div>
              </div>
            </div>

            {/* 相关专题 */}
            <div>
              <h3 className="font-heading font-bold text-lg text-foreground-950 mb-5">
                相关专题
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {related.map((sp) => (
                  <Link
                    key={sp.id}
                    to={`/special?id=${sp.id}`}
                    className="group flex gap-4 items-center rounded-lg border border-background-200 bg-background-50 p-4 hover:border-primary-300 transition-colors"
                  >
                    <div className="shrink-0 w-24 h-16 rounded-md overflow-hidden bg-background-100">
                      <img
                        src={sp.cover}
                        alt={sp.title}
                        title={`${sp.title} - WWW.17C.COM`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="min-w-0">
                      <span className="block font-heading font-semibold text-base text-foreground-950 group-hover:text-primary-600 transition-colors">
                        {sp.title}
                      </span>
                      <span className="mt-1 block text-xs text-foreground-500 line-clamp-2">
                        {sp.intro}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}